"use client";

import { LinearGradient } from "@visx/gradient";
import { Group } from "@visx/group";
import { scaleBand, scaleLinear } from "@visx/scale";
import { max } from "d3-array";
import { motion } from "framer-motion";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import type { TooltipData, YFormatType } from "../../../context/ChartContext";
import { ChartAxis } from "../ChartAxis";
import { ChartTooltip } from "../ChartTooltip";

export interface CategoryDataPoint {
  /** Category label (x-axis) */
  label: string;
  /** Numeric value (y-axis) */
  value: number;
  /** Optional color override for this bar */
  color?: string;
}

export interface CategoryBarChartProps {
  /** Array of data points with labels and values */
  data: CategoryDataPoint[];
  /** Width of the chart (auto-resizes to container if not provided) */
  width?: number;
  /** Height of the chart (auto-resizes to container if not provided) */
  height?: number;
  /** Margin around the chart area */
  margin?: { top: number; right: number; bottom: number; left: number };
  /** Single color for all bars (uses theme color if not provided) */
  color?: string;
  /** Formatter for numeric axis values (uses standard chart formatting system) */
  yFormatter?: YFormatType;
  /** Show grid lines */
  showGrid?: boolean;
  /** Show axis labels */
  showAxes?: boolean;
  /** Animate bars on mount */
  animate?: boolean;
  /** Bar corner radius */
  barRadius?: number;
  /** Inner padding between bars (0-1) */
  barPadding?: number;
  /** Callback when a bar is clicked */
  onBarClick?: (dataPoint: CategoryDataPoint, index: number) => void;
  /** Callback when a bar is hovered */
  onBarHover?: (dataPoint: CategoryDataPoint | null, index: number | null) => void;
  /** Optional external active bar index */
  activeIndex?: number | null;
  /** Additional className */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
  /** Accessible label */
  "aria-label"?: string;
  /** Orientation: vertical (default) or horizontal */
  orientation?: "vertical" | "horizontal";
  /** Show tooltip on hover (default true) */
  showTooltip?: boolean;
}

const defaultMargin = { top: 20, right: 20, bottom: 50, left: 60 };

export const CategoryBarChart: React.FC<CategoryBarChartProps> = ({
  data,
  width: propWidth,
  height: propHeight,
  margin = defaultMargin,
  color,
  yFormatter = { type: "number" },
  showGrid = true,
  showAxes = true,
  animate = true,
  barRadius = 4,
  barPadding = 0.3,
  onBarClick,
  onBarHover,
  activeIndex,
  className,
  style,
  "aria-label": ariaLabel = "Category bar chart",
  orientation = "vertical",
  showTooltip = true,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const [tooltipLabel, setTooltipLabel] = useState<string | null>(null);
  const [actualWidth, setActualWidth] = useState(propWidth ?? 600);
  const [actualHeight, setActualHeight] = useState(propHeight ?? 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const resolvedActiveIndex = activeIndex ?? hoveredIndex;
  const gradientId = useId();
  const baseColor = color || "var(--color-viz-default)";

  useEffect(() => {
    if (propWidth !== undefined && propHeight !== undefined) {
      setActualWidth(propWidth);
      setActualHeight(propHeight);
      return;
    }

    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (propWidth === undefined) {
          setActualWidth(entry.contentRect.width);
        }
        if (propHeight === undefined) {
          setActualHeight(entry.contentRect.height);
        }
      }
    });

    resizeObserver.observe(containerRef.current);
    if (propWidth === undefined) {
      setActualWidth(containerRef.current.offsetWidth);
    }
    if (propHeight === undefined) {
      setActualHeight(containerRef.current.offsetHeight || 300);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [propWidth, propHeight]);

  const { xScale, yScale, innerWidth, innerHeight } = useMemo(() => {
    const innerWidth = actualWidth - margin.left - margin.right;
    const innerHeight = actualHeight - margin.top - margin.bottom;

    if (orientation === "horizontal") {
      const xMax = max(data, (d) => d.value) || 0;

      return {
        xScale: scaleLinear<number>({
          range: [0, innerWidth],
          domain: [0, xMax * 1.1],
          nice: true,
        }),
        yScale: scaleBand<string>({
          range: [0, innerHeight],
          domain: data.map((d) => d.label),
          padding: barPadding,
        }),
        innerWidth,
        innerHeight,
      };
    }

    const yMax = max(data, (d) => d.value) || 0;

    return {
      xScale: scaleBand<string>({
        range: [0, innerWidth],
        domain: data.map((d) => d.label),
        padding: barPadding,
      }),
      yScale: scaleLinear<number>({
        range: [innerHeight, 0],
        domain: [0, yMax * 1.1],
        nice: true,
      }),
      innerWidth,
      innerHeight,
    };
  }, [data, actualWidth, actualHeight, margin, barPadding, orientation]);

  if (data.length === 0) {
    return (
      <div
        ref={containerRef}
        className={twMerge(
          "flex h-full min-h-[200px] w-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-background-surface text-sm font-medium text-text-muted dark:border-gray-700",
          className
        )}
        style={propHeight !== undefined ? { height: propHeight, ...style } : style}
      >
        No data available
      </div>
    );
  }

  const handleBarHover = (index: number | null, dataPoint: CategoryDataPoint | null) => {
    setHoveredIndex(index);
    onBarHover?.(dataPoint, index);

    if (index !== null && dataPoint) {
      // Calculate tooltip position based on bar position
      let tooltipX: number;
      let tooltipY: number;

      if (orientation === "horizontal") {
        const barWidth = (xScale as ReturnType<typeof scaleLinear<number>>)(dataPoint.value);
        const barY = (yScale as ReturnType<typeof scaleBand<string>>)(dataPoint.label) || 0;
        const barHeight = (yScale as ReturnType<typeof scaleBand<string>>).bandwidth();
        tooltipX = margin.left + barWidth;
        tooltipY = margin.top + barY + barHeight / 2;
      } else {
        const bandScale = xScale as ReturnType<typeof scaleBand<string>>;
        const linearScale = yScale as ReturnType<typeof scaleLinear<number>>;
        const barX = bandScale(dataPoint.label) ?? 0;
        const barWidth = bandScale.bandwidth();
        const barY = linearScale(dataPoint.value);
        tooltipX = margin.left + barX + barWidth / 2;
        tooltipY = margin.top + barY;
      }

      const shouldShowLeft = tooltipX > actualWidth / 2;
      setTooltipLabel(dataPoint.label);
      setTooltipData({
        xValue: new Date(), // Dummy date, label is passed separately
        series: [
          {
            label: "Value",
            value: dataPoint.value,
            color: dataPoint.color || baseColor,
          },
        ],
        x: tooltipX,
        y: tooltipY,
        position: shouldShowLeft ? "left" : "right",
      });
    } else {
      setTooltipData(null);
      setTooltipLabel(null);
    }
  };

  return (
    <div
      ref={containerRef}
      className={twMerge("relative h-full w-full overflow-hidden rounded-lg bg-background-surface", className)}
      style={propHeight !== undefined ? { height: propHeight, ...style } : style}
      aria-label={ariaLabel}
      role="img"
    >
      <svg width={actualWidth} height={actualHeight}>
        <defs>
          <LinearGradient id={`${gradientId}-bar`} from={baseColor} to={baseColor} toOpacity={0.85} />
        </defs>

        <Group left={margin.left} top={margin.top}>
          {showGrid && (
            <g className="grid-lines">
              {(orientation === "horizontal"
                ? (xScale as ReturnType<typeof scaleLinear<number>>).ticks(5)
                : (yScale as ReturnType<typeof scaleLinear<number>>).ticks(5)
              ).map((tickValue) => {
                const y =
                  orientation === "horizontal" ? 0 : (yScale as ReturnType<typeof scaleLinear<number>>)(tickValue);
                const x =
                  orientation === "horizontal" ? (xScale as ReturnType<typeof scaleLinear<number>>)(tickValue) : 0;
                return (
                  <line
                    key={tickValue}
                    x1={orientation === "horizontal" ? x : 0}
                    y1={orientation === "horizontal" ? 0 : y}
                    x2={orientation === "horizontal" ? x : innerWidth}
                    y2={orientation === "horizontal" ? innerHeight : y}
                    stroke="var(--color-border-default)"
                    strokeOpacity={0.5}
                    strokeDasharray="2,3"
                  />
                );
              })}
            </g>
          )}

          {data.map((d, i) => {
            const isActive = resolvedActiveIndex === i || resolvedActiveIndex == null;
            const barColor = d.color || `url(#${gradientId}-bar)`;

            if (orientation === "horizontal") {
              const barWidth = (xScale as ReturnType<typeof scaleLinear<number>>)(d.value);
              const barHeight = (yScale as ReturnType<typeof scaleBand<string>>).bandwidth();
              const barY = (yScale as ReturnType<typeof scaleBand<string>>)(d.label) || 0;

              return (
                <motion.rect
                  key={d.label}
                  x={0}
                  y={barY}
                  width={barWidth}
                  height={barHeight}
                  fill={barColor}
                  rx={barRadius}
                  ry={barRadius}
                  initial={animate ? { width: 0, opacity: 0.5 } : undefined}
                  animate={{ width: barWidth, opacity: isActive ? 1 : 0.5 }}
                  transition={{
                    duration: 0.5,
                    delay: i * 0.05,
                    ease: "easeOut",
                    opacity: { duration: 0.15, delay: 0 },
                  }}
                  style={{
                    cursor: onBarClick || onBarHover ? "pointer" : "default",
                  }}
                  onMouseEnter={() => handleBarHover(i, d)}
                  onMouseLeave={() => handleBarHover(null, null)}
                  onClick={() => onBarClick?.(d, i)}
                />
              );
            }

            const bandScale = xScale as ReturnType<typeof scaleBand<string>>;
            const linearScale = yScale as ReturnType<typeof scaleLinear<number>>;

            const barWidth = bandScale.bandwidth();
            const barX = bandScale(d.label) ?? 0;
            const barY = linearScale(d.value);
            const barHeight = Math.max(0, innerHeight - barY);

            if (animate) {
              return (
                <motion.rect
                  key={d.label}
                  x={barX}
                  width={barWidth}
                  fill={barColor}
                  rx={barRadius}
                  ry={barRadius}
                  initial={{ y: innerHeight, height: 0, opacity: 0.5 }}
                  animate={{
                    y: barY,
                    height: barHeight,
                    opacity: isActive ? 1 : 0.5,
                  }}
                  transition={{
                    duration: 0.5,
                    delay: i * 0.05,
                    ease: "easeOut",
                    opacity: { duration: 0.15, delay: 0 },
                  }}
                  style={{
                    cursor: onBarClick || onBarHover ? "pointer" : "default",
                  }}
                  onMouseEnter={() => handleBarHover(i, d)}
                  onMouseLeave={() => handleBarHover(null, null)}
                  onClick={() => onBarClick?.(d, i)}
                />
              );
            }

            return (
              <rect
                key={d.label}
                x={barX}
                y={barY}
                width={barWidth}
                height={barHeight}
                fill={barColor}
                rx={barRadius}
                ry={barRadius}
                opacity={isActive ? 1 : 0.5}
                style={{
                  cursor: onBarClick || onBarHover ? "pointer" : "default",
                  transition: "opacity 150ms ease-out",
                }}
                onMouseEnter={() => handleBarHover(i, d)}
                onMouseLeave={() => handleBarHover(null, null)}
                onClick={() => onBarClick?.(d, i)}
              />
            );
          })}

          {showAxes && orientation === "vertical" && (
            <>
              <ChartAxis.CategoryBottom top={innerHeight} scale={xScale as ReturnType<typeof scaleBand<string>>} />
              <ChartAxis.Left
                left={0}
                scale={yScale as ReturnType<typeof scaleLinear<number>>}
                yFormatter={yFormatter}
                height={innerHeight}
              />
            </>
          )}

          {showAxes && orientation === "horizontal" && (
            <>
              <ChartAxis.NumericBottom
                top={innerHeight}
                scale={xScale as ReturnType<typeof scaleLinear<number>>}
                yFormatter={yFormatter}
                width={innerWidth}
              />
              <ChartAxis.CategoryLeft left={0} scale={yScale as ReturnType<typeof scaleBand<string>>} />
            </>
          )}
        </Group>
      </svg>

      {/* Tooltip */}
      {showTooltip && <ChartTooltip data={tooltipData} formatter={yFormatter} label={tooltipLabel ?? undefined} />}
    </div>
  );
};

export type { CategoryDataPoint as CategoryBarChartDataPoint };
